import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';

interface ContentState {
  contents: Record<string, Record<string, any>>;
  loading: boolean;
  error: string | null;
  fetchContent: (pageId: string) => Promise<void>;
  updateContent: (pageId: string, key: string, content: any, type: string) => Promise<void>;
}

export const useContent = create<ContentState>((set, get) => ({
  contents: {},
  loading: false,
  error: null,
  fetchContent: async (pageId: string) => {
    set({ loading: true });
    try {
      // First get the page id from the slug
      const { data: pageData, error: pageError } = await supabase
        .from('pages')
        .select('id')
        .eq('slug', pageId)
        .single();

      if (pageError) throw pageError;

      const { data, error } = await supabase
        .from('content_blocks')
        .select('*')
        .eq('page_id', pageData.id);

      if (error) throw error;

      const contents = data.reduce((acc, block) => ({
        ...acc,
        [block.key]: block.content
      }), {});

      set({ contents, loading: false });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
    }
  },
  updateContent: async (pageId: string, key: string, content: any, type: string) => {
    try {
      // Get the page id from the slug
      let { data: pageData, error: pageError } = await supabase
        .from('pages')
        .select('id')
        .eq('slug', pageId)
        .single();

      if (pageError) throw pageError;

      // Check if content block exists
      const { data: existingBlock, error: checkError } = await supabase
        .from('content_blocks')
        .select('id')
        .eq('page_id', pageData.id)
        .eq('key', key)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      let error;
      if (existingBlock) {
        // Update existing block
        const { error: updateError } = await supabase
          .from('content_blocks')
          .update({
            content,
            type,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingBlock.id);
        error = updateError;
      } else {
        // Insert new block
        const { error: insertError } = await supabase
          .from('content_blocks')
          .insert({
          page_id: pageData.id,
          key,
          content,
          type
        });
        error = insertError;
      }

      if (error) throw error;

      set(state => ({
        contents: {
          ...state.contents,
          [key]: content
        }
      }));
    } catch (error) {
      console.error('Error updating content:', error);
    }
  }
}));